import Board from "components/Common/Board/Board";
import React, { useState } from "react";
import { Table } from "components/Common/Table/Table";
import UserTableRow from "./UserTableRow";
import styled from "styled-components";
import { PageFilterPanel } from "components/Common/PageFilterPannel";
import FilterSearch from "components/Common/PageFilterPannel/FilterSearch";
import Pagination from "components/foundation/Pagination";
import { FormProvider, useForm } from "react-hook-form";
import useUsersSearchQuery from "features/user/hooks/queries/useUsersSearchQuery";
import useUsersAutocompleteQuery from "features/user/hooks/queries/useUsersAutocompleteQuery";
import { match } from "ts-pattern";
import SearchEmpty from "components/Common/Empty/SearchEmpty";
import dayjs from "dayjs";
import DateRangePicker from "components/foundation/DateRangePicker";
import FilterListItem from "components/Common/PageFilterPannel/FilterListItem";
import removeEmptyFields from "utils/removeEmptyFields";
import { LOCAL_TIME_FORMAT } from "config/constant";

interface FormData {
  searchType: "userName" | "companyName";
  search: string;
  startDate?: Date;
  endDate?: Date;
}

const UserDashboardWebPage = () => {
  const form = useForm<FormData>({
    defaultValues: {
      searchType: "userName",
      search: "",
      startDate: undefined,
      endDate: undefined,
    },
  });

  const [searchOption, setSearchOption] = useState<FormData>({
    searchType: "userName",
    search: "",
    startDate: undefined,
    endDate: undefined,
  });
  const [page, setPage] = useState(1);

  const { data } = useUsersSearchQuery(
    removeEmptyFields({
      page: page - 1,
      size: 10,
      [searchOption.searchType]: searchOption.search,
      endDate:
        searchOption.endDate &&
        dayjs(searchOption.endDate).format(LOCAL_TIME_FORMAT),
      startDate:
        searchOption.startDate &&
        dayjs(searchOption.startDate).format(LOCAL_TIME_FORMAT),
    }),
  );

  const [type, keyword] = [form.watch("searchType"), form.watch("search")];

  const { data: autoCompleteData } = useUsersAutocompleteQuery({
    type,
    keyword,
  });

  const usersList = data.data.result;

  const handleSearch = (data: FormData) => {
    setSearchOption(data);
  };

  return (
    <UserDashboardPageLayout>
      <UserDashboardPageHeader>
        <h1>USER MANAGEMENT</h1>
      </UserDashboardPageHeader>

      <form onSubmit={form.handleSubmit(handleSearch)}>
        <FormProvider {...form}>
          <PageFilterPanel.Board>
            <PageFilterPanel.NestItem>
              <FilterListItem title="등록일">
                <DateRangePicker
                  onChangeDate={({ start, end }) => {
                    setSearchOption((pre) => ({
                      ...pre,
                      startDate: start,
                      endDate: end,
                    }));
                  }}
                />
              </FilterListItem>
              <FilterSearch
                autocomplete={autoCompleteData?.data.result.results.map((i) => {
                  return {
                    value: i,
                    label: i,
                  };
                })}
                dropdownList={[
                  {
                    label: "이름",
                    value: "userName",
                  },
                  {
                    label: "회사",
                    value: "companyName",
                  },
                ]}
              />
            </PageFilterPanel.NestItem>
          </PageFilterPanel.Board>
        </FormProvider>
      </form>

      <ContentBoard>
        <Table.TotalCount>총 {usersList.totalElements}건</Table.TotalCount>

        {match(usersList.totalElements)
          .with(0, () => <SearchEmpty />)
          .otherwise(() => (
            <>
              <Table.Layout>
                <Table.Table columns="auto auto auto auto auto auto auto auto auto">
                  <Table.Thead>
                    <Table.Th>No.</Table.Th>
                    <Table.Th>이메일</Table.Th>
                    <Table.Th>이름</Table.Th>
                    <Table.Th>연락처</Table.Th>
                    <Table.Th>회사</Table.Th>
                    <Table.Th>부서</Table.Th>
                    <Table.Th>직급</Table.Th>
                    <Table.Th>가입일</Table.Th>
                    <Table.Th>승인여부</Table.Th>
                  </Table.Thead>
                  <Table.Tbody>
                    {usersList.contents.map((user, index) => (
                      <UserTableRow
                        id={user.uuid}
                        key={user.uuid}
                        no={
                          data.data.result.totalElements -
                          (index + (page - 1) * 10)
                        }
                        name={user.name}
                        email={user.email}
                        phone={user.phoneNumber}
                        company={user.companyName}
                        department={user.companyDepartment}
                        position={user.companyPosition}
                        joinDate={user.createdAt}
                        userStatus={user.approveStatus}
                      />
                    ))}
                  </Table.Tbody>
                </Table.Table>
              </Table.Layout>
              <Pagination
                currentPage={page}
                onClickPageButton={(page) => setPage(page)}
                lastPage={usersList.totalPages}
                style={{
                  marginLeft: "auto",
                }}
              />
            </>
          ))}
      </ContentBoard>
    </UserDashboardPageLayout>
  );
};

export default UserDashboardWebPage;

const UserDashboardPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const UserDashboardPageHeader = styled(Board)`
  padding: 3rem 4rem;

  h1 {
    font-size: 28px;
    font-weight: 600;
    line-height: 36px; /* 128.571% */
    letter-spacing: -0.56px;
  }
`;

const ContentBoard = styled(Board)`
  padding: 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;
